import { scopeProjectRef } from "@cmmd-forge/client-runtime";
import {
  EnvironmentId,
  ModelSelection,
  ProviderInteractionMode,
  RuntimeMode,
  ScopedProjectRef,
  ServerProvider,
} from "@cmmd-forge/contracts";
import {
  fetchForgeProviderAccess,
  resolveDefaultProviderAccountId,
  type ForgeProviderAccessState,
  type ProviderCredentialMode,
} from "../../cmmd/forgeProviderAccess";
import { resolveProviderAccountIdentity } from "@cmmd-forge/shared/providerAccountIdentity";
import type { CloudProjectCatalogRow } from "../../cmmd/cmmdForgeRuntimeListResource";
import type {
  ComposerFileAttachment,
  ComposerImageAttachment,
  DraftThreadEnvMode,
} from "../../composerDraftStore";
import type { TerminalContextDraft } from "../../lib/terminalContext";
import type { useHandleNewThread } from "../../hooks/useHandleNewThread";
import { resolveNewThreadRuntimeProjectRef } from "../../lib/threadRuntimeTarget";
import { selectProjectsAcrossEnvironments, useStore } from "../../store";
import type { Project } from "../../types";
import type { CloudRuntimeConnectProgressEvent } from "../sidebar/connectCloudRuntime";
import { prefersComposerViewTransition } from "../chat/composerViewTransition";
import {
  resolveNewThreadModelSelection,
  resolveScratchTargetEnvironmentId,
} from "./NewThreadLanding.logic";
import { connectCloudCatalogRow } from "./NewThreadLanding.cloud";
import { waitForAuthorizedProviderLaunch } from "../../lib/providerInventoryReadiness";
import { ensureDesktopRuntimeEnvironmentApi } from "../../lib/ensureDesktopRuntimeEnvironmentApi";
import { ensureProjectForCwd } from "../../lib/addProject";
import { readEnvironmentApi } from "../../environmentApi";
import { resolveEligibleLaunchProvider } from "../../providerModels";
import { hydrateCreatedDraft } from "./NewThreadDraftHydration";
import { runNewThreadImageTransfer } from "./runNewThreadImageTransfer";
import { resolveSubmissionRuntimeId } from "./NewThreadLanding.runtimeIdentity";
export { readLiveProviderInventory } from "./liveProviderInventory";
import { readLiveProviderInventory } from "./liveProviderInventory";

interface ProviderInventoryConfig {
  readonly providers: ReadonlyArray<ServerProvider>;
}

// A freshly-connected runtime (most commonly a just-booted cloud sprite) can
// report a connected, healthy socket before its own local Forge server has
// finished discovering which providers/models it can actually execute. That
// discovery is in-process work (spawning/parsing provider CLIs), not another
// network hop, so it normally completes in low single-digit seconds; 20s
// gives generous headroom over that without leaving a genuinely broken
// runtime's user staring at a spinner for anywhere near the ~90s bound
// reserved for the whole VM coming up (see CMMD_RUNTIME_METADATA_TIMEOUT_MS
// in environments/runtime/service.ts). The wait re-reads live store state on
// every attempt (the real "has the runtime published a non-empty, executable
// model inventory" signal — see isServerProviderExecutable), so it resolves
// the instant the real signal arrives instead of sleeping out a fixed delay.
export interface SubmitNewThreadInput {
  /**
   * The user chose "Don't use a project". Authoritative: it takes priority
   * over every project-shaped field below, which may still hold a stale
   * selection the user has since opted out of.
   */
  readonly noWorkspaceSelected: boolean;
  /**
   * True when the primary environment only proxies to an execution plane rather
   * than being one — the Forge Cloud control surface. Its scratch directory
   * would land on the shell container, so it is never a valid bucket host.
   */
  readonly primaryIsWorkspaceProxy: boolean;
  readonly activeProject: Project | null;
  readonly activeCloudCatalogRow: CloudProjectCatalogRow | null;
  readonly activeDesktopInventoryProject: DesktopRuntimeInventoryProjectSelection | null;
  readonly selectedExecutionId: string;
  readonly primaryEnvironmentId: EnvironmentId | null;
  readonly primaryServerConfig: ProviderInventoryConfig | null;
  readonly selectedModelSelection: ModelSelection | null;
  readonly credentialMode: ProviderCredentialMode;
  readonly envMode: DraftThreadEnvMode;
  readonly branch: string;
  readonly includeBranch: boolean;
  readonly prompt: string;
  readonly images?: ReadonlyArray<ComposerImageAttachment>;
  readonly files?: ReadonlyArray<ComposerFileAttachment>;
  readonly terminalContexts?: ReadonlyArray<TerminalContextDraft>;
  readonly onImagesTransferStarted?:
    | ((images: ReadonlyArray<ComposerImageAttachment>) => void)
    | undefined;
  readonly onImagesTransferCancelled?:
    | ((images: ReadonlyArray<ComposerImageAttachment>) => void)
    | undefined;
  readonly onImagesTransferred?:
    | ((images: ReadonlyArray<ComposerImageAttachment>) => void)
    | undefined;
  readonly onSubmissionHydrated?: (() => void) | undefined;
  readonly interactionMode: ProviderInteractionMode;
  readonly runtimeMode: RuntimeMode;
  readonly handleNewThread: ReturnType<typeof useHandleNewThread>["handleNewThread"];
  readonly onProgress: (event: CloudRuntimeConnectProgressEvent) => void;
}

export interface DesktopRuntimeInventoryProjectSelection {
  readonly runtimeId: string;
  readonly runtimeLabel: string;
  readonly projectId: string;
  readonly projectName: string;
}

interface SubmissionContext {
  readonly projectRef: ScopedProjectRef;
  readonly targetProject: Project;
  readonly modelSelection: ModelSelection;
}

export async function submitNewThread(input: SubmitNewThreadInput): Promise<void> {
  const submissionRef = await resolveSubmissionRef(input);
  const context = await resolveSubmissionContext(input, submissionRef);
  const images = input.images ?? [];
  const files = input.files ?? [];
  const terminalContexts = input.terminalContexts ?? [];
  await runNewThreadImageTransfer({
    images,
    onTransferStarted: input.onImagesTransferStarted,
    onTransferCancelled: input.onImagesTransferCancelled,
    createThread: () =>
      input.handleNewThread(context.projectRef, {
        envMode: input.envMode,
        ...(input.includeBranch ? { branch: input.branch } : {}),
        // The hero composer is on screen right now, so the route change can morph it
        // down into its docked position rather than cutting to a new page.
        ...(prefersComposerViewTransition() ? { viewTransition: true } : {}),
      }),
    hydrateDraft: () => {
      const hydrated = hydrateCreatedDraft({
        projectRef: context.projectRef,
        targetProject: context.targetProject,
        modelSelection: context.modelSelection,
        interactionMode: input.interactionMode,
        runtimeMode: input.runtimeMode,
        prompt: input.prompt,
        images,
        files,
        terminalContexts,
      });
      if (hydrated) {
        input.onSubmissionHydrated?.();
        input.onImagesTransferred?.(images);
      }
      return hydrated;
    },
  });
}

/**
 * Ask the runtime for its no-project bucket, turning "this sprite is too old"
 * into a sentence a user can act on.
 *
 * Sprites clone Forge at PROVISION time and the fleet updates one runtime per
 * reconciler tick behind a quiet window, so a mixed-version fleet is the normal
 * state for a while after any merge. A sprite provisioned before
 * `workspace.ensureScratch` existed rejects the call as an unknown method; left
 * raw that surfaces as an opaque RPC error against a menu item the user was
 * just offered.
 */
async function ensureScratchWorkspaceOnRuntime(
  api: NonNullable<ReturnType<typeof readEnvironmentApi>>,
): Promise<{ readonly workspaceRoot: string; readonly title: string }> {
  try {
    return await api.workspace.ensureScratch({});
  } catch (cause: unknown) {
    const message = cause instanceof Error ? cause.message : String(cause);
    if (/not found|unknown method|unsupported|no such method/i.test(message)) {
      throw new Error(
        "This runtime is on an older Forge build that cannot host a no-project chat yet. It updates automatically — try again shortly, or pick a different runtime.",
        { cause },
      );
    }
    throw cause;
  }
}

/**
 * Resolve the scratch workspace on the runtime the app is already connected to,
 * and the project that points at it.
 *
 * The primary environment is the right target on both surfaces: in Forge Cloud
 * the browser is talking to the sprite itself, and on Desktop it is the local
 * server. That keeps this path clear of cloud-runtime provisioning, which needs
 * a catalog row a no-workspace chat does not have.
 *
 * The project is deduped by the scratch `cwd`, so a second standalone chat
 * reuses the first one's project rather than stacking a new scratch project per
 * thread.
 */
async function resolveScratchProjectRef(input: SubmitNewThreadInput): Promise<ScopedProjectRef> {
  const targetEnvironmentId = resolveScratchTargetEnvironmentId({
    primaryEnvironmentId: input.primaryEnvironmentId,
    primaryIsWorkspaceProxy: input.primaryIsWorkspaceProxy,
    selectedExecutionId: input.selectedExecutionId,
  });
  if (targetEnvironmentId === null) {
    throw new Error("Choose which runtime should host this chat before starting it.");
  }
  const environmentId = EnvironmentId.make(targetEnvironmentId);
  const api = readEnvironmentApi(environmentId);
  if (!api) {
    throw new Error("The runtime is connected but has not exposed its Forge API yet.");
  }
  const scratch = await ensureScratchWorkspaceOnRuntime(api);
  const { projectId } = await ensureProjectForCwd({
    api,
    environmentId,
    projects: selectProjectsAcrossEnvironments(useStore.getState()),
    cwd: scratch.workspaceRoot,
    title: scratch.title,
    // The target here IS the primary environment (see above), so its reported
    // providers are the right ones to pick this project's default model from.
    ...(input.primaryServerConfig === null
      ? {}
      : { providers: input.primaryServerConfig.providers }),
  });
  return scopeProjectRef(environmentId, projectId);
}

async function resolveSubmissionRef(input: SubmitNewThreadInput): Promise<ScopedProjectRef> {
  if (input.noWorkspaceSelected) {
    return resolveScratchProjectRef(input);
  }
  if (input.activeCloudCatalogRow !== null) {
    return connectCloudCatalogRow(input.activeCloudCatalogRow, { onProgress: input.onProgress });
  }
  if (input.activeProject !== null) {
    return scopeProjectRef(input.activeProject.environmentId, input.activeProject.id);
  }
  if (input.activeDesktopInventoryProject !== null) {
    const selection = input.activeDesktopInventoryProject;
    const environmentId = EnvironmentId.make(selection.runtimeId);
    await ensureDesktopRuntimeEnvironmentApi({
      environmentId,
      primaryEnvironmentId: input.primaryEnvironmentId,
      label: selection.runtimeLabel,
    });
    const attachedProject = selectProjectsAcrossEnvironments(useStore.getState()).find(
      (project) => project.environmentId === environmentId && project.id === selection.projectId,
    );
    if (!attachedProject) {
      throw new Error(
        `${selection.runtimeLabel} connected, but ${selection.projectName} is no longer available. Refresh runtime inventory and retry.`,
      );
    }
    return scopeProjectRef(attachedProject.environmentId, attachedProject.id);
  }
  throw new Error("Choose a project before starting a thread.");
}

async function resolveSubmissionContext(
  input: SubmitNewThreadInput,
  submissionRef: ScopedProjectRef,
): Promise<SubmissionContext> {
  const latestProjects = selectProjectsAcrossEnvironments(useStore.getState());
  // A scratch ref is already concrete — it names the environment its directory
  // was just created on. Re-running the runtime remap could retarget it at a
  // different environment, where that path does not exist.
  const projectRef = input.noWorkspaceSelected
    ? submissionRef
    : resolveNewThreadRuntimeProjectRef({
        requestedRuntime: input.selectedExecutionId === "cloud" ? "cloud" : "desktop-local",
        primaryEnvironmentId: input.primaryEnvironmentId,
        projects: latestProjects,
        projectRef: submissionRef,
      });
  const targetProject =
    latestProjects.find(
      (project) =>
        project.environmentId === projectRef.environmentId && project.id === projectRef.projectId,
    ) ?? input.activeProject;
  if (targetProject === null) {
    throw new Error("The owning runtime connected, but its project snapshot was not ready.");
  }

  return {
    projectRef,
    targetProject,
    modelSelection: await resolveSubmissionModelSelection(input, projectRef.environmentId),
  };
}

/** Re-reads live provider/model inventory for `environmentId` — the real
 * "has the runtime told us what it can execute" signal — never a cached
 * snapshot from submission start. */
function resolveModelSelectionForEnvironment(
  input: SubmitNewThreadInput,
  providers: readonly ServerProvider[],
  providerAccess: Extract<ForgeProviderAccessState, { status: "ready" }>,
): ModelSelection | null {
  const preferredProvider = input.selectedModelSelection?.provider ?? null;
  if (
    preferredProvider !== null &&
    resolveEligibleLaunchProvider(
      providers,
      preferredProvider,
      providerAccess,
      input.credentialMode,
    ) === null
  ) {
    return null;
  }
  const selection = resolveNewThreadModelSelection({
    providers,
    preferredProvider,
    preferredModel: input.selectedModelSelection?.model ?? null,
    pinPreferredSelection: input.selectedModelSelection !== null,
    providerAccess,
    credentialMode: input.credentialMode,
  });
  return applyProviderAccountId(selection, providerAccess, input.selectedModelSelection);
}

function applyProviderAccountId(
  selection: ModelSelection | null,
  providerAccess: Extract<ForgeProviderAccessState, { status: "ready" }>,
  preferredSelection: ModelSelection | null,
): ModelSelection | null {
  if (selection === null) {
    return selection;
  }
  if (selection.provider !== "codex" && selection.provider !== "claudeAgent") return selection;
  const selectedProviderAccountId =
    selection.provider === preferredSelection?.provider
      ? preferredSelection.providerAccountId
      : selection.providerAccountId;
  if (selectedProviderAccountId) {
    const providerAccounts = (providerAccess.providerAccounts ?? []).filter(
      (account) => account.provider === selection.provider,
    );
    const canonicalProviderAccountId = resolveProviderAccountIdentity(
      providerAccounts,
      selectedProviderAccountId,
    );
    return {
      ...selection,
      providerAccountId: canonicalProviderAccountId,
    };
  }
  const providerAccountId = resolveDefaultProviderAccountId(providerAccess, selection.provider);
  return providerAccountId ? { ...selection, providerAccountId } : selection;
}

/**
 * Wait (bounded) for the target runtime to publish an executable model
 * inventory instead of failing the instant a just-connected runtime hasn't
 * said what it can run yet. `resolveModelSelectionForEnvironment` re-reads
 * live store state on every attempt, so this resolves the moment
 * `environments/runtime/connection.ts` delivers the real `providerStatuses`
 * readiness signal — it never gates on a timer or on "connect succeeded".
 */
async function resolveSubmissionModelSelection(
  input: SubmitNewThreadInput,
  environmentId: EnvironmentId,
): Promise<ModelSelection> {
  const runtimeId = resolveSubmissionRuntimeId(input, environmentId);
  return waitForAuthorizedProviderLaunch({
    loadProviderAccess: () => fetchForgeProviderAccess(input.credentialMode, runtimeId),
    readProviders: () => readLiveProviderInventory(input, environmentId),
    credentialMode: input.credentialMode,
    pinnedProvider: input.selectedModelSelection?.provider ?? null,
    resolve: ({ providerAccess, providers }) =>
      resolveModelSelectionForEnvironment(input, providers, providerAccess),
  });
}

/**
 * Exported for its test only. The picker and this function are the read seam
 * and the write seam of one decision, and a test that covered only the picker
 * let the two drift once already (FOR-36439).
 */
